import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import PlusIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { debounce } from 'debounce';
import React, { useState, useContext } from 'react';
import DeploymentActionDialog from './DeploymentActionDialog';
import TestActionDialog from './TestActionDialog';
import WorkflowAccessPermissionsEditor from './WorkflowAccessPermissionsEditor';
import WorkflowCustomTagsEditor from './WorkflowCustomTagsEditor';
import WorkflowNotificationsEditor from './WorkflowNotificationsEditor';
import WorkflowPreApproversEditor from './WorkflowPreApproversEditor';
import classNames from 'classnames';
import AwsRegions from '../../../common/AwsRegions';
import MobxContext from '../../../mobx/MobxContext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      padding: 24,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    section: {
      marginBottom: 24
    },
    sectionHeader: {
      marginBottom: 8,
    },
    deploymentActionsSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    addButton: {
      textTransform: 'none'
    },
    tableHead: {
      backgroundColor: 'inherit'
    },
    actionLink: {
      textAlign: 'left'
    },
    deploymentActionButtons: {
      minWidth: 130
    },
    largeGutter: {
      marginBottom: 16
    },
    defaultIamRoleTextField: {
      marginBottom: 24,
      width: 500
    },
    defaultParamSetContainer: {
      display: 'flex',
      alignItems: 'flex-end'
    },
    defaultParamSetTextField: {
      marginRight: 24,
      flex: 1,
      minWidth: 200,
      maxWidth: 500
    },
    defaultParamSetVersionTextField: {
      flex: 1,
      minWidth: 100,
      maxWidth: 300
    },
    maxWorkflowTimeTextField: {
      marginBottom: 16,
      display: 'block'
    },
    maxAwsRetriesTextField: {
      display: 'block'
    },
    scheduledTimeTextField: {
      marginTop: 16
    },
    viewParamSetLink: { 
      marginLeft: 16 
    }
  })
);

interface Props {
  workflow: any;
  family: any;
  tests: any[];
  groups: any;
  environments: any;
  showErrors: boolean;
  onWorkflowChange: (workflow: any) => void;
}

function WorkflowBuilderTab(props: Props) {
  const {
    workflow,
    groups,
    tests,
    environments,
    showErrors,
    onWorkflowChange
  } = props;

  const classes = useStyles({});
  const [deploymentActionToEdit, setDeploymentActionToEdit] = useState(null);
  const [testActionToEdit, setTestActionToEdit] = useState(null);
  const { environmentStore } = useContext(MobxContext);

  const debouncedOnWorkflowChange = debounce(onWorkflowChange, 200);

  const onCloseDeploymentActionDialog = () => {
    setDeploymentActionToEdit(null);
  };

  const onDeploymentActionSelected = (action) => {
    setDeploymentActionToEdit(action);
  };

  const onDeploymentActionChange = (updatedAction: any) => {
    const index = workflow.DeploymentRecipe.findIndex(item => item === deploymentActionToEdit);
    const newWorkflow = { ...workflow };
    if (index === -1) {
      newWorkflow.DeploymentRecipe.push(updatedAction);
    } else {
      newWorkflow.DeploymentRecipe[index] = updatedAction;
      // Check if the action name has changed so any dependencies can be updated
      const originalActionName = deploymentActionToEdit.ActionName;
      const newActionName = updatedAction.ActionName;
      if (originalActionName !== newActionName) {
        // Update any dependent deployment actions
        newWorkflow.DeploymentRecipe = workflow.DeploymentRecipe.map(action => {
          const dependencyIndex = action.Dependencies.indexOf(originalActionName);
          if (dependencyIndex !== -1) {
            const actionCopy = { ...action };
            actionCopy.Dependencies[dependencyIndex] = newActionName;
            return actionCopy;
          } else {
            return action;
          }
        });

        // Update any dependent test actions
        newWorkflow.TestFunctions = workflow.TestFunctions.map(action => {
          const dependencyIndex = action.Dependencies.indexOf(originalActionName);
          if (dependencyIndex !== -1) {
            const actionCopy = { ...action };
            actionCopy.Dependencies[dependencyIndex] = newActionName;
            return actionCopy;
          } else {
            return action;
          }
        });
      }
    }
    onWorkflowChange(newWorkflow);
    setDeploymentActionToEdit(null);
  };

  const onAddDeploymentAction = () => {
    setDeploymentActionToEdit({
      'DeploymentName': '',
      'DeckName': '',
      'IgnoreExistingParameters': false,
      'DisableStackRollback': false,
      'Update': false,
      'Global': false,
      'ActionName': '',
      'ActionGroup': '',
      'CapabilityFlags': [],
      'AssumeRoleARN': '',
      'TeardownAssumeRoleARN': '',
      'SkipStacks': false,
      'Tag': '',
      'Dependencies': [],
      'ParameterSets': [],
      'Parameters': {},
      'MaxWorkflowTime': 4,
      'EnableScheduledTests': false,
      'DisableTeardown': false
    });
  };

  const onRemoveDeploymentAction = (actionToRemove) => {
    const index = workflow.DeploymentRecipe.findIndex(item => item.ActionName === actionToRemove.ActionName);
    const newWorkflow = { ...workflow };
    newWorkflow.DeploymentRecipe.splice(index, 1);
    onWorkflowChange(newWorkflow);
  };

  const onTestActionSelected = (action) => {
    setTestActionToEdit(action);
  };

  const onCloseTestActionDialog = () => {
    setTestActionToEdit(null);
  };

  const onAddTestAction = () => {
    setTestActionToEdit({
      'ActionName': '',
      'TestName': '',
      'SecurityGroups': [],
      'Subnets': [],
      'Version': '',
      'Dependencies': [],
      'ParameterSets': [],
      'Parameters': {}
    });
  };

  const onRemoveTestAction = (actionToRemove) => {
    const index = workflow.TestFunctions.findIndex(item => item.ActionName === actionToRemove.ActionName);
    const newWorkflow = { ...workflow };
    newWorkflow.TestFunctions.splice(index, 1);
    onWorkflowChange(newWorkflow);
  };

  const onTestActionChange = (updatedAction: any) => {
    const index = workflow.TestFunctions.findIndex(item => item === testActionToEdit);
    const newWorkflow = { ...workflow };
    if (index === -1) {
      newWorkflow.TestFunctions.push(updatedAction);
    } else {
      newWorkflow.TestFunctions[index] = updatedAction;
    }
    onWorkflowChange(newWorkflow);
    setTestActionToEdit(null);
  };

  const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedOnWorkflowChange({ ...workflow, [event.target.name]: event.target.value });
  };

  const onNumberFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onWorkflowChange({ ...workflow, [event.target.name]: parseInt(event.target.value) });
  };

  const onEnvironmentChange = (event: React.ChangeEvent<{}>, value: any) => {
    onWorkflowChange({ ...workflow, AccountName: value ? value.EnvironmentName : undefined });
  };

  const onRegionChange = (event: React.ChangeEvent<{}>, value: any) => {
    onWorkflowChange({ ...workflow, Region: value ? value.code : undefined });
  };

  const onDefaultParamSetNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const updatedParamSet = workflow.DefaultParameterSet ? { Name: value, Version: workflow.DefaultParameterSet.Version } : { Name: value };
    debouncedOnWorkflowChange({
      ...workflow,
      DefaultParameterSet: updatedParamSet.Name || updatedParamSet.Version ? updatedParamSet : undefined
    });
  };

  const onDefaultParamSetVersionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const updatedParamSet = workflow.DefaultParameterSet ? { Version: value, Name: workflow.DefaultParameterSet.Name } : { Version: value };
    debouncedOnWorkflowChange({
      ...workflow,
      DefaultParameterSet: updatedParamSet.Name || updatedParamSet.Version ? updatedParamSet : undefined
    });
  };

  const onWorkflowAccessChange = (workflowAccess) => {
    onWorkflowChange({
      ...workflow,
      WorkflowAccess: workflowAccess
    });
  };

  const onEnableScheduledTestsChanged = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onWorkflowChange({ ...workflow, EnableScheduledTests: checked, DailyScheduledExecutionTime: checked ? '02:00' : '' });
  };

  const onWorkflowNotifiersChange = (notifiers) => {
    onWorkflowChange({ ...workflow, Notifiers: notifiers });
  };

  const onWorkflowCustomTagsChange = (tags) => {
    onWorkflowChange({ ...workflow, CustomTags: tags });
  };

  const onWorkflowPreApproversChange = (approvers) => {
    onWorkflowChange({ ...workflow, Approvers: { PreApprovers: approvers } });
  };

  const environment = workflow.AccountName ? environmentStore.getEnvironmentByName(workflow.AccountName) : null;
  const accountId = environment ? environment.AccountID : null;

  return (
    <div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Workflow Details</Typography>
        </div>
        <div className={classes.panel}>
          <Grid container spacing={8}>
            <Grid item xs={12} md={4}>
              <TextField
                required
                name='Name'
                label='Name'
                error={showErrors && !workflow.Name}
                helperText={showErrors && !workflow.Name ? 'Name is required' : ''}
                defaultValue={workflow.Name}
                onChange={onTextFieldChange}
                fullWidth
                placeholder='Workflow Name'
                InputLabelProps={{
                  shrink: true
                }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={environments}
                value={workflow.AccountName || null}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.EnvironmentName}
                onChange={onEnvironmentChange}
                getOptionSelected={(option, value) => value ? option.EnvironmentName === value : false}
                renderInput={(params) => <TextField
                  {...params} label='Account'
                  error={showErrors && !workflow.AccountName}
                  helperText={showErrors && !workflow.AccountName ? 'Account is required' : ''}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={AwsRegions}
                value={workflow.Region || null}
                onChange={onRegionChange}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.code}
                getOptionSelected={(option, value) => value ? option.code === value : false}
                renderInput={(params) => <TextField
                  {...params}
                  label='Region'
                  error={showErrors && !workflow.Region}
                  helperText={showErrors && !workflow.Region ? 'Region is required' : ''}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes.section}>
        <div className={classNames(classes.sectionHeader, classes.deploymentActionsSectionHeader)}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Deployment Actions</Typography>
          {workflow.DeploymentRecipe && workflow.DeploymentRecipe.length > 0 &&
            <Button className={classes.addButton} color='primary' onClick={onAddDeploymentAction}><PlusIcon /> Add Deployment Action</Button>}
        </div>
        <div className={classes.panel}>
          {(!workflow.DeploymentRecipe || workflow.DeploymentRecipe.length === 0) &&
            <Button className={classes.addButton} color='primary' onClick={onAddDeploymentAction}><PlusIcon /> Add Deployment Action</Button>}
          {workflow.DeploymentRecipe && workflow.DeploymentRecipe.length > 0 && <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Deck</TableCell>
                  <TableCell>Deployment</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflow.DeploymentRecipe.map(action => <TableRow key={action.ActionName}>
                  <TableCell>
                    <Link
                      className={classes.actionLink}
                      component='button'
                      variant='body2'
                      onClick={() => onDeploymentActionSelected(action)}
                    >
                      {action.ActionName}
                    </Link>

                  </TableCell>
                  <TableCell>
                    {action.DeckName}
                  </TableCell>
                  <TableCell>
                    {action.DeploymentName}
                  </TableCell>
                  <TableCell align='right' className={classes.deploymentActionButtons}>
                    <IconButton onClick={() => onDeploymentActionSelected(action)}><EditIcon /></IconButton>
                    <IconButton onClick={() => onRemoveDeploymentAction(action)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>}
        </div>
      </div>
      <div className={classes.section}>
        <div className={classNames(classes.sectionHeader, classes.deploymentActionsSectionHeader)}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Test Actions</Typography>
          {workflow.TestFunctions && workflow.TestFunctions.length > 0 &&
            <Button className={classes.addButton} color='primary' onClick={onAddTestAction}><PlusIcon /> Add Test Action</Button>}
        </div>
        <div className={classes.panel}>
          {(!workflow.TestFunctions || workflow.TestFunctions.length === 0) &&
            <Button className={classes.addButton} color='primary' onClick={onAddTestAction}><PlusIcon /> Add Test Action</Button>}
          {workflow.TestFunctions && workflow.TestFunctions.length > 0 && <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Test</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflow.TestFunctions.map(action => <TableRow key={action.ActionName}>
                  <TableCell>
                    <Link
                      className={classes.actionLink}
                      component='button'
                      variant='body2'
                      onClick={() => onTestActionSelected(action)}
                    >
                      {action.ActionName}
                    </Link>

                  </TableCell>
                  <TableCell>
                    {action.TestName}
                  </TableCell>
                  <TableCell>
                    {action.Version}
                  </TableCell>
                  <TableCell align='right' className={classes.deploymentActionButtons}>
                    <IconButton onClick={() => onTestActionSelected(action)}><EditIcon /></IconButton>
                    <IconButton onClick={() => onRemoveTestAction(action)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>}
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Action Defaults</Typography>
        </div>
        <div className={classes.panel}>
          <Typography variant='subtitle1' className={classes.largeGutter}>Default IAM Deployment Role</Typography>
          <TextField
            id="DefaultDeploymentIAMRole"
            name='DefaultDeploymentIAMRole'
            label='Role ARN'
            size='medium'
            autoComplete="on"
            defaultValue={workflow.DefaultDeploymentIAMRole}
            onChange={onTextFieldChange}
            className={classes.defaultIamRoleTextField}
            InputLabelProps={{
              shrink: true
            }} />
          <Typography variant='subtitle1' className={classes.largeGutter}>Default Parameter Set</Typography>
          <div className={classes.defaultParamSetContainer}>
            <TextField
              label='Parameter Set Name'
              size='medium'
              defaultValue={workflow.DefaultParameterSet ? workflow.DefaultParameterSet.Name : undefined}
              onChange={onDefaultParamSetNameChange}
              className={classes.defaultParamSetTextField}
              InputLabelProps={{
                shrink: true
              }} />
            <TextField
              label='Parameter Set Version'
              size='medium'
              defaultValue={workflow.DefaultParameterSet ? workflow.DefaultParameterSet.Version : undefined}
              onChange={onDefaultParamSetVersionChange}
              className={classes.defaultParamSetVersionTextField}
              InputLabelProps={{
                shrink: true
              }} />
            {accountId && workflow.Region && workflow.DefaultParameterSet && workflow.DefaultParameterSet.Name && workflow.DefaultParameterSet.Version && <Link
              className={classes.viewParamSetLink}
              variant="body1"
              target="_blank"
              rel="noopener noreferrer"
              href={`/faro/parameters/${accountId}/${workflow.Region}/${workflow.DefaultParameterSet.Name}?version=${workflow.DefaultParameterSet.Version}`}
            >
              View Parameter Set
            </Link>}
          </div>
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Access Permissions</Typography>
        </div>
        <WorkflowAccessPermissionsEditor
          workflowAccess={workflow.WorkflowAccess}
          groups={groups}
          onWorkflowAccessChange={onWorkflowAccessChange}
        />
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Settings</Typography>
        </div>
        <div className={classes.panel}>
          <Grid container spacing={8}>
            <Grid item xs={6}>
              <TextField
                name='MaxWorkflowTime'
                label='Max Workflow Time (hours)'
                type="number"
                value={workflow.MaxWorkflowTime}
                fullWidth
                onChange={onNumberFieldChange}
                className={classes.maxWorkflowTimeTextField}
                InputLabelProps={{
                  shrink: true
                }} />
              <TextField
                name='MaxAWSRetries'
                label='Max AWS Retries (0-12)'
                type="number"
                value={workflow.MaxAWSRetries}
                onChange={onNumberFieldChange}
                className={classes.maxAwsRetriesTextField}
                fullWidth
                InputLabelProps={{
                  shrink: true
                }} />
            </Grid>
            <Grid item xs={6}>
              <Typography color="textSecondary" variant="caption" display="block">Options</Typography>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workflow.DisableTeardown}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onWorkflowChange({ ...workflow, DisableTeardown: checked })}
                      name="enableTerminationProtection"
                      color={workflow.DisableTeardown ? 'primary' : 'default'}
                    />
                  }
                  label="Enable Termination Protection"
                />
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workflow.EnableScheduledTests}
                      onChange={onEnableScheduledTestsChanged}
                      name="enableTerminationProtection"
                      color={workflow.EnableScheduledTests ? 'primary' : 'default'}
                    />
                  }
                  label="Enable Daily Scheduled Tests"
                />
                {workflow.EnableScheduledTests && <div className={classes.scheduledTimeTextField}>
                  <TextField
                    id="time"
                    label="Scheduled Time (UTC)"
                    type="time"
                    name="DailyScheduledExecutionTime"
                    defaultValue={workflow.DailyScheduledExecutionTime}
                    onChange={onTextFieldChange}
                    helperText="Customize a scheduled time (default time is 02:00, all executions will run in UTC Time)"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300
                    }}
                  />
                </div>}
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Notifications</Typography>
        </div>
        <WorkflowNotificationsEditor notifiers={workflow.Notifiers || []} onWorkflowNotifiersChange={onWorkflowNotifiersChange} />
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>PreApprovers</Typography>
        </div>
        <WorkflowPreApproversEditor
          approvers={workflow.Approvers && workflow.Approvers.PreApprovers ? workflow.Approvers.PreApprovers : []}
          groups={groups}
          onWorkflowApproversChange={onWorkflowPreApproversChange}
        />
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Custom Tags</Typography>
        </div>
        <WorkflowCustomTagsEditor customTags={workflow.CustomTags || []} onWorkflowCustomTagsChange={onWorkflowCustomTagsChange} />
      </div>
      {
        deploymentActionToEdit && <DeploymentActionDialog
          accountId={accountId}
          region={workflow.Region}
          action={deploymentActionToEdit}
          deploymentActions={workflow.DeploymentRecipe.filter(action => action.ActionName !== deploymentActionToEdit.ActionName)}
          testActions={workflow.TestFunctions}
          onClose={onCloseDeploymentActionDialog}
          onChange={onDeploymentActionChange}
        />
      }
      {
        testActionToEdit && <TestActionDialog
          accountId={accountId}
          region={workflow.Region}
          familyId={workflow.Family}
          tests={tests}
          action={testActionToEdit}
          testActions={workflow.TestFunctions.filter(action => action.ActionName !== testActionToEdit.ActionName)}
          deploymentActions={workflow.DeploymentRecipe}
          onClose={onCloseTestActionDialog}
          onChange={onTestActionChange}
        />
      }
    </div>
  );
};

export default WorkflowBuilderTab;
